/**
 * Which hub pages have something on them, for the sections that link to hubs.
 *
 * A city or category hub with nothing in it renders `noindex,follow`. Every
 * internal link to one is a page Google crawls, finds empty and files under
 * "Soft 404" or "Excluded by noindex", and the cross-link sections multiplied
 * that by every city and all seven locales: 2,149 soft 404s and 3,321 noindex
 * pages in Search Console on 2026-10-09. So a hub is linked only when the
 * data on the page already says it holds at least one place.
 */
import { isRankedCategory, rankCategories } from "./category-rank";

export interface HubLinkTarget {
  slug: string;
  name: string;
  count: number;
}

/** One row of `/categories/explore`, as far as counting goes. */
export interface CategoryCountRow {
  slug: string;
  name: string;
  parentSlug?: string | null;
  /** Published places directly in this category (in the city, when asked for one). */
  itemsCount?: number;
}

/**
 * Top-level categories with at least one published place, busiest first.
 *
 * `itemsCount` counts places directly in a category, so a parent with all its
 * places in subcategories (Activities in Fez: 0 itself, a quad and a buggy
 * under it) is rolled up from its children rather than read as empty.
 */
export function stockedRootCategories(rows: readonly CategoryCountRow[]): HubLinkTarget[] {
  const parents = new Map(rows.map((r) => [r.slug, r.parentSlug ?? null]));
  const rootOf = (slug: string) => {
    let current = slug;
    for (let i = 0; i < 5; i++) {
      const parent = parents.get(current);
      if (!parent) return current;
      current = parent;
    }
    return current;
  };
  const counts = new Map<string, number>();
  for (const row of rows) {
    const root = rootOf(row.slug);
    counts.set(root, (counts.get(root) ?? 0) + (row.itemsCount ?? 0));
  }
  const roots = rows.filter((r) => !r.parentSlug && isRankedCategory(r.slug) && (counts.get(r.slug) ?? 0) > 0);
  return rankCategories(roots, counts).map((r) => ({ slug: r.slug, name: r.name, count: counts.get(r.slug) ?? 0 }));
}

/** One row of `/cities/explore`, as far as counting goes. */
export interface CityCountRow {
  slug: string;
  name: string;
  /** Published places of every kind in the city, bookable and explore. */
  itemsCount?: number;
}

/** Cities whose hub (`/place/{city}`) has something on it, most first. */
export function stockedCities(rows: readonly CityCountRow[]): HubLinkTarget[] {
  return rows
    .filter((c) => (c.itemsCount ?? 0) > 0)
    .map((c) => ({ slug: c.slug, name: c.name, count: c.itemsCount ?? 0 }))
    .sort((a, b) => b.count - a.count || a.slug.localeCompare(b.slug));
}

/**
 * The cities a category hub's own listing holds places in, most first.
 *
 * Only a page of the listing, so it can miss a city, but it can never name
 * one with nothing in it: every city here has a place on screen.
 */
export function citiesInListing(
  items: readonly { city?: { slug?: string | null; name?: string | null } | null }[],
): HubLinkTarget[] {
  const bySlug = new Map<string, HubLinkTarget>();
  for (const item of items) {
    const slug = item.city?.slug;
    if (!slug) continue;
    const hit = bySlug.get(slug);
    if (hit) hit.count += 1;
    else bySlug.set(slug, { slug, name: item.city?.name || slug, count: 1 });
  }
  return [...bySlug.values()].sort((a, b) => b.count - a.count || a.slug.localeCompare(b.slug));
}
